// Tarjeta fija «Videollamada con X» 5 minutos antes de una cita virtual, con SONIDO (Sala CRM↔HCE, rebanada 8b; PLAN 4.10 y 9-C4/C6).
//
// Se monta UNA vez en `App`, hermana de `AvisosSala`: visible desde cualquier pantalla del HCE. No es un barrido del servidor: el servidor da la lista
// de citas virtuales próximas del médico (`GET /api/hce/telemedicina/proximas`) y SU hora (`ahora`); el temporizador corre aquí con esa hora (nunca con
// el reloj del equipo para decidir). La tarjeta se queda hasta que el médico la cierra o se conecta; el sonido suena al aparecer y una vez más a los 60 s.
// Todas las reglas (qué cita, cuándo suena, quién avisa entre pestañas) viven en lib/avisoTelemedicina.ts y se prueban allí.
//
// Con varias pestañas abiertas, UNA sola avisa: la que tiene el bloqueo de «pestaña visible»; si ninguna está visible, la que tiene el de respaldo.
// Este aviso es independiente del «Paciente X llegó» (presencial), que sigue sin sonido.
import { useMedplumProfile } from '@medplum/react-hooks';
import { X } from 'lucide-react';
import type { JSX } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { notify } from '@/components/ui/sonner';
import {
  avisosDebidos,
  cerrarAviso,
  claveAviso,
  crearAlmacenTolerante,
  crearReproductor,
  debeActuar,
  hayQueSonar,
  instalarDesbloqueoDeAudio,
  leerCerrados,
  sonarSiToca,
} from '@/lib/avisoTelemedicina';
import { bloqueoEnUso, bloqueosDelNavegador, nombreDeBloqueo, useLiderDeAvisos, useLiderDeSegundoPlano } from '@/lib/liderAvisos';
import { ErrorSala, horaEcuadorDe } from '@/lib/salaHce';
import { ahoraServidorMs, conectarYAbrir, obtenerVirtualesProximas, type CitaVirtual } from '@/lib/telemedicinaHce';
import { useEventosSala } from '@/lib/useEventosSala';

const CLAVE_LIDER_VISIBLE = 'lc-telemed-aviso-visible';
const CLAVE_LIDER_RESPALDO = 'lc-telemed-aviso-respaldo';
const TICK_MS = 1000;
const TICKS_POR_REANCLAJE = 60;
/** Si confirmar con el servidor falla, no se vuelve a intentar cada segundo: espera este tiempo. */
const ESPERA_TRAS_FALLO_MS = 10_000;

const reloj = (): number => (typeof performance !== 'undefined' ? performance.now() : Date.now());

export function AvisoTelemedicina(): JSX.Element | null {
  // Sin sesión de Medplum (landing, login) no hay nada que consultar.
  const profile = useMedplumProfile();
  return profile ? <AvisoTelemedicinaActivo /> : null;
}

const sinClave = <V,>(o: Record<string, V>, clave: string): Record<string, V> => Object.fromEntries(Object.entries(o).filter(([k]) => k !== clave));

type Instantanea = { citas: CitaVirtual[]; ahoraIso: string; recibidoEnMs: number };
type EnlaceBloqueado = { cita: CitaVirtual; link: string };

function AvisoTelemedicinaActivo(): JSX.Element | null {
  const { t } = useTranslation();
  const almacen = useMemo(() => crearAlmacenTolerante(), []);
  const reproductor = useMemo(() => crearReproductor(), []);
  const [datos, setDatos] = useState<Instantanea>({ citas: [], ahoraIso: '', recibidoEnMs: 0 });
  /** Sube cada vez que ESTA pestaña cierra un aviso; los cierres de otras pestañas se leen del almacén compartido en cada tick. */
  const [versionCierres, setVersionCierres] = useState(0);
  const [ahoraMs, setAhoraMs] = useState(reloj);
  const [conectando, setConectando] = useState<ReadonlySet<string>>(new Set());
  const [errores, setErrores] = useState<Record<string, string>>({});
  const [enlaces, setEnlaces] = useState<Record<string, EnlaceBloqueado>>({});
  /** Una cuenta sin telemedicina (403: no es médico) no vuelve a consultar. */
  const sinTelemedicina = useRef(false);
  const sonando = useRef(false);
  const ultima = useRef(0);
  /** Instante (reloj monotónico) del último intento fallido de confirmar con el servidor antes de sonar: no se reintenta antes de ESPERA_TRAS_FALLO_MS. */
  const ultimoFallo = useRef<number | null>(null);

  const liderVisible = useLiderDeAvisos(true, CLAVE_LIDER_VISIBLE);
  const liderRespaldo = useLiderDeSegundoPlano(true, CLAVE_LIDER_RESPALDO);
  const roles = useRef({ liderVisible, liderRespaldo });
  roles.current = { liderVisible, liderRespaldo };

  /** Pide la lista al servidor. Devuelve lo recibido (o `null` si falló) para que quien lo pidió decida con datos frescos, sin esperar al render. */
  const cargar = useCallback(async (): Promise<Instantanea | null> => {
    if (sinTelemedicina.current) return null;
    const mia = ++ultima.current;
    try {
      const r = await obtenerVirtualesProximas();
      const nueva: Instantanea = { citas: Array.isArray(r.citas) ? r.citas : [], ahoraIso: r.ahora, recibidoEnMs: reloj() };
      if (mia === ultima.current) setDatos(nueva);
      return nueva;
    } catch (err) {
      if (err instanceof ErrorSala && err.status === 403) {
        sinTelemedicina.current = true;
        setDatos({ citas: [], ahoraIso: '', recibidoEnMs: 0 });
      }
      // 401 o fallo de red/5xx: se conserva lo último que se vio; el respaldo de 30 s reintenta.
      return null;
    }
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);
  // Una cita creada, reagendada, cancelada o ya conectada llega como aviso del canal en vivo (y el respaldo de 30 s lo repite): la lista se vuelve a pedir.
  useEventosSala(() => void cargar(), { tipos: ['sala', 'calendario'] });

  useEffect(() => instalarDesbloqueoDeAudio(reproductor), [reproductor]);
  // Al desmontar (o cerrar sesión) se libera el AudioContext.
  useEffect(() => () => reproductor.cerrar(), [reproductor]);

  const hayCitas = datos.citas.length > 0;
  useEffect(() => {
    if (!hayCitas) return;
    let ticks = 0;
    const h = setInterval(() => {
      setAhoraMs(reloj());
      // Cada minuto, la pestaña que avisa vuelve a anclar su cuenta a la hora del servidor: el canal en vivo y el respaldo de 30 s solo corren con la pestaña
      // visible, y un equipo que durmió pudo dejar atrasado el reloj monotónico. Las demás pestañas no consultan (no se multiplica el tráfico por pestaña).
      ticks += 1;
      if (ticks % TICKS_POR_REANCLAJE === 0 && (roles.current.liderVisible || roles.current.liderRespaldo)) void cargar();
    }, TICK_MS);
    return () => clearInterval(h);
  }, [hayCitas, cargar]);

  const ahoraSrvMs = ahoraServidorMs(datos.ahoraIso, datos.recibidoEnMs, ahoraMs);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `versionCierres` fuerza la relectura de los avisos cerrados
  const debidos = useMemo(() => avisosDebidos(datos.citas, ahoraSrvMs, leerCerrados(almacen)), [datos.citas, ahoraSrvMs, almacen, versionCierres]);

  // Sonido: en cada tick, si a algún aviso le toca sonar y ESTA pestaña es la que avisa, se confirma con el servidor que la cita sigue en pie (una
  // cancelada o «No asistió» no debe sonar) y suena.
  useEffect(() => {
    if (ahoraSrvMs === null || sonando.current || debidos.length === 0) return;
    if (!hayQueSonar(debidos, ahoraSrvMs, almacen)) return;
    if (!roles.current.liderVisible && !roles.current.liderRespaldo) return;
    if (ultimoFallo.current !== null && reloj() - ultimoFallo.current < ESPERA_TRAS_FALLO_MS) return;
    sonando.current = true;
    void (async () => {
      try {
        const hayLiderVisible = roles.current.liderVisible ? true : await bloqueoEnUso(bloqueosDelNavegador(), nombreDeBloqueo(CLAVE_LIDER_VISIBLE));
        if (!debeActuar({ ...roles.current, hayLiderVisible })) return;
        const fresca = await cargar();
        if (!fresca) {
          ultimoFallo.current = reloj();
          return;
        }
        ultimoFallo.current = null;
        const ahoraFresco = ahoraServidorMs(fresca.ahoraIso, fresca.recibidoEnMs, reloj());
        if (ahoraFresco === null) return;
        const debidosFrescos = avisosDebidos(fresca.citas, ahoraFresco, leerCerrados(almacen));
        await sonarSiToca({
          debidos: debidosFrescos,
          ahoraSrvMs: ahoraFresco,
          almacen,
          reproductor,
          alBloqueado: () => notify({ color: 'yellow', message: t('sala.avisoVirtual.activarSonido'), autoClose: 12000 }),
        });
      } finally {
        sonando.current = false;
      }
    })();
  }, [ahoraSrvMs, debidos, almacen, reproductor, cargar, t]);

  function cerrar(c: CitaVirtual): void {
    cerrarAviso(almacen, claveAviso(c));
    setVersionCierres((v) => v + 1);
    setEnlaces((e) => sinClave(e, claveAviso(c)));
  }

  async function conectar(c: CitaVirtual): Promise<void> {
    const clave = claveAviso(c);
    if (conectando.has(clave)) return;
    setConectando((s) => new Set(s).add(clave));
    setErrores((e) => sinClave(e, clave));
    try {
      // La ventana se abre en el clic, antes de esperar al servidor (ver `conectarYAbrir`).
      const r = await conectarYAbrir(c.citaId, 'programada');
      cerrarAviso(almacen, clave);
      setVersionCierres((v) => v + 1);
      if (!r.abierta) setEnlaces((e) => ({ ...e, [clave]: { cita: c, link: r.link } }));
    } catch (err) {
      const e = err instanceof ErrorSala ? err : new ErrorSala('No se pudo conectar.', 0, 'error');
      setErrores((m) => ({ ...m, [clave]: e.message }));
    } finally {
      setConectando((s) => {
        const n = new Set(s);
        n.delete(clave);
        return n;
      });
      void cargar();
    }
  }

  // Solo la pestaña líder visible pinta la tarjeta (con varias pestañas, una sola). Las demás pestañas guardan los registros compartidos y se mantienen al día.
  if (!liderVisible) return null;
  const tarjetas = [...debidos, ...Object.values(enlaces).map((e) => e.cita).filter((c) => !debidos.some((d) => claveAviso(d) === claveAviso(c)))];
  if (tarjetas.length === 0) return null;

  return (
    <div className="pointer-events-none fixed bottom-4 right-4 z-50 flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-2">
      {tarjetas.map((c) => {
        const clave = claveAviso(c);
        const enlace = enlaces[clave];
        return (
          <section
            key={clave}
            role="alert"
            data-aviso-virtual={c.citaId}
            className="pointer-events-auto rounded-lg border border-hce-pine bg-card p-3 shadow-lg"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <h2 className="truncate text-sm font-semibold text-hce-ink">{t('sala.avisoVirtual.titulo', { nombre: c.pacienteNombre })}</h2>
                <p className="mt-0.5 font-mono text-xs text-hce-muted">{t('sala.avisoVirtual.hora', { hora: horaEcuadorDe(c.inicio) })}</p>
              </div>
              <button
                type="button"
                className="rounded p-1 text-hce-muted hover:bg-secondary hover:text-hce-ink focus-visible:outline focus-visible:outline-2"
                aria-label={t('sala.avisoVirtual.cerrar')}
                onClick={() => cerrar(c)}
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
            {errores[clave] && <p className="mt-2 text-xs text-red-700">{errores[clave]}</p>}
            {enlace ? (
              <p className="mt-2 text-xs text-hce-ink">
                {t('sala.avisoVirtual.ventanaBloqueada')}{' '}
                <a className="font-medium underline" href={enlace.link} target="_blank" rel="noopener noreferrer" onClick={() => cerrar(c)}>
                  {t('sala.avisoVirtual.abrirVideollamada')}
                </a>
              </p>
            ) : (
              <Button className="mt-2 w-full" size="sm" disabled={conectando.has(clave)} onClick={() => void conectar(c)}>
                {conectando.has(clave) ? t('sala.avisoVirtual.conectando') : t('sala.avisoVirtual.conectarAhora')}
              </Button>
            )}
          </section>
        );
      })}
    </div>
  );
}
